import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LibItem, UserShortInfo } from "../../types";
import ManShortSelector from "../../components/ui/shortItemSelect";
import { libBackend, backend, GroupData } from "../../services";
import { RootState } from "../../state/reducer";
import { toast } from "sonner";
import MultiCheckbox from "../../components/ui/multiCheckbox";
import { statusNames } from "../../constants/events";
import {
  defaultTaskFilters,
  taskFiltersCacheKey,
  TaskListRequestParams,
} from "../../forms/filters/task";
import Calendar from "icons/calendar";
import DateInput from "../../components/ui/dateInput";
import { SearchIcon } from "../../icons";
import { currentUserGroupFilter } from "../../state/functions";

interface TaskFilterModalProps {
  onApplyFilters: (filters: TaskListRequestParams) => void;
  closeModal: () => void;
}

const TaskFilterModal: React.FC<TaskFilterModalProps> = ({
  onApplyFilters,
  closeModal,
}) => {
  const State = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const [usersToSelect, setUsersToSelect] = useState<UserShortInfo[]>([]);

  const [statusList, setStatusList] = useState<LibItem[]>(statusNames);
  const groupList = State.groupsToSelect;

  const rights = State.rights;
  const session = State.session;
  // action_id 5, 6
  const accessGroup = rights.rang < 3 || rights.permissions.indexOf(5) > -1;
  const accessAll = rights.rang < 3 || rights.permissions.indexOf(6) > -1;

  const [filters, setFilters] = useState<TaskListRequestParams>({
    ...defaultTaskFilters,
    user_id: !accessAll ? 0 : !accessGroup ? session?.id : undefined,
    group_id: accessAll ? 0 : accessGroup ? rights.group_ids[0] : undefined,
  });
  9;

  useEffect(() => {
    const cachedFilters = localStorage.getItem(taskFiltersCacheKey);
    if (cachedFilters) {
      const lastFilters: TaskListRequestParams = JSON.parse(cachedFilters);
      setFilters(lastFilters);
    } else {
      setFilters(State.taskListRequestParams);
    }
  }, [groupList]);

  useEffect(() => {
    backend.getAllUsersShorts({ group_id: filters.group_id }).then((res) => {
      setUsersToSelect(res.users);
    });
  }, [filters.group_id]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleParamChange = (name: keyof TaskListRequestParams, value: any) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiCheckboxChange = (name: string, selectedIds: number[]) => {
    setFilters((prev) => ({ ...prev, [name]: selectedIds }));
  };

  const applyFilters = () => {
    const dataToCache = JSON.stringify(filters);
    localStorage.setItem(taskFiltersCacheKey, dataToCache);
    onApplyFilters(filters);
    closeModal();
  };

  return (
    <div className="creationForm filterForm">
      <div className="modalHeading">
        <h3>Фильтры задач</h3>
      </div>
      <form className="columnCenter">
        <div className="flex formRow dateFromTo">
          <div className="fieldName">Задача создана</div>
          <div className="fieldInput flex between gap24">
            <DateInput
              value={filters.date_create_min || ""}
              onSelect={(dateTime) =>
                handleParamChange("date_create_min", dateTime)
              }
            />
            <DateInput
              value={filters.date_create_max || ""}
              onSelect={(dateTime) =>
                handleParamChange("date_create_max", dateTime)
              }
            />
          </div>
        </div>
        <div className="flex formRow dateFromTo">
          <div className="fieldName">Дата завершения</div>
          <div className="fieldInput flex between gap24">
            <DateInput
              value={filters.date_complete_min || ""}
              onSelect={(dateTime) =>
                handleParamChange("date_complete_min", dateTime)
              }
            />
            <DateInput
              value={filters.date_complete_max || ""}
              onSelect={(dateTime) =>
                handleParamChange("date_complete_max", dateTime)
              }
            />
          </div>
        </div>
        <div className="flex formRow">
          <div className="fieldName">Группа</div>
          <div className="fieldInput flex">
            <select
              id="order_dir"
              name="order_dir"
              value={filters.group_id}
              onChange={(event) =>
                setFilters((prev) => {
                  console.log("Changed group");
                  console.log(event.target.value);
                  return {
                    ...prev,
                    group_id: Number(event.target.value),
                  };
                })
              }
            >
              {accessAll && <option value="0">Любая</option>}
              {groupList.map((item, index) => {
                return (
                  <option key={index} value={item.id}>
                    {item.name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className="flex formRow">
          <div className="fieldName flex gap12">
            <input
              className="checkBoxInput"
              type="checkbox"
              checked={filters.search_in_subgroup || false}
              onChange={(e) =>
                handleParamChange("search_in_subgroup", e.target.checked)
              }
            />
            <span>Искать в подгруппах</span>
          </div>
        </div>
        {(accessAll || accessGroup) && (
          <div className="flex formRow">
            <div className="fieldName">Ответственный</div>
            <div className="fieldInput flex">
              <ManShortSelector
                items={usersToSelect}
                enabledAll={true}
                onChangeCallback={(id) =>
                  setFilters((prev) => ({ ...prev, user_id: id }))
                }
              />
            </div>
          </div>
        )}
        <div className="flex formRow">
          <div className="fieldName">Состояние</div>
          <div className="fieldInput flex">
            <MultiCheckbox
              items={statusList}
              selectedIds={filters.status_ids || []}
              onChange={(selectedIds) =>
                handleMultiCheckboxChange("status_ids", selectedIds)
              }
            />
          </div>
        </div>
        <div className="flex formRow">
          <div className="fieldName">Направление сортировки</div>
          <div className="fieldInput flex">
            <select
              id="order_dir"
              name="order_dir"
              value={filters.order_dir}
              onChange={handleInputChange}
            >
              <option value="ASC">По возрастанию</option>
              <option value="DESC">По убыванию</option>
            </select>
          </div>
        </div>
        <div className="flex formRow">
          <div className="fieldName">Параметр сортировки</div>
          <div className="fieldInput flex">
            <select
              id="order_param"
              name="order_param"
              value={filters.order_param}
              onChange={handleInputChange}
            >
              <option value="date_start">Дата начала</option>
              <option value="date_end">Дата окончания</option>
            </select>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="flex btnRow">
          <button className="filterButton" type="button" onClick={applyFilters}>
            <SearchIcon width={32} height={32} />
            <span>Искать</span>
          </button>
          <button className="cancelButton" type="button" onClick={closeModal}>
            Отменить
          </button>
        </div>
      </form>
    </div>
  );
};

export default TaskFilterModal;
